import React, { useEffect, useId, useRef, useState } from "react";
import { MapPin, AirplaneTilt, AirplaneTakeoff, AirplaneLanding, MagnifyingGlass } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeApp";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { searchFormL } from "@/i18n/search";
import useStayCities from "@/hooks/useStayCities";

// "Saint-Louis", "saint louis" and "Saint Louis" are one name; accents and
// case never decide a match.
const fold = (text) => String(text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** True when the typed text starts the name or one of its words ("louis" finds Saint-Louis). */
export function nameMatches(name, typed) {
  const want = fold(typed);
  if (!want) return false;
  const have = fold(name);
  return have.startsWith(want) || have.split(" ").some((word, i, words) => words.slice(i).join(" ").startsWith(want));
}

/**
 * The stays destination's suggestions: Toukky's own stay cities that match
 * first, then the lookup's places as cities (an airport stands for its city,
 * one entry per city and country), only those whose name the text starts:
 * a fuzzy neighbour (Jakarta for "Dakar") is no destination. Never a code.
 */
export function staySuggestions(typed, ownCities = [], found = []) {
  const seen = new Set();
  const out = [];
  const add = (entry) => {
    const key = `${fold(entry.city)}|${fold(entry.country)}`;
    const cityKey = fold(entry.city);
    if (!entry.city || seen.has(key) || seen.has(cityKey)) return;
    seen.add(key);
    if (entry.type === "stay") seen.add(cityKey);
    out.push(entry);
  };
  for (const own of ownCities) {
    if (nameMatches(own.city, typed)) add({ type: "stay", code: "", city: own.city, name: "", country: own.region || "" });
  }
  for (const place of found) {
    const city = String(place?.city || place?.name || "").trim();
    if (!nameMatches(city, typed)) continue;
    add({ type: "city", code: "", city, name: "", country: String(place?.country || "").trim() });
  }
  return out;
}

/**
 * Autocomplete input backed by Amadeus /api/locations/autocomplete (with static fallback).
 * Props:
 *   value: current input string
 *   onChange: (val: string) => void
 *   onSelect: (loc: {code, city, name, country, label}) => void
 *   icon: 'pin' | 'plane' | 'takeoff' | 'landing' | 'search' (visual only)
 *   placeholder, label, testId
 *   subType: sent as sub_type ("AIRPORT" limits the lookup to airports)
 *   pickValue: what fills the field on a pick — the IATA code by default,
 *            which is what the flight search sends upstream
 */
export default function LocationAutocomplete({ value, onChange, onSelect, icon = "pin", placeholder, label, testId, naked = false, subType, pickValue, compact = false, required = false, stays = false, openOnFocus = true }) {
  const settings = useSettings();
  const words = pick(searchFormL, settings?.lang || "fr");
  const searching = words.searching;
  // The stays destination: city names only (see staySuggestions).
  // Read once the person types, never for a value the form arrived with.
  const [typedOnce, setTypedOnce] = useState(false);
  const ownCities = useStayCities({ enabled: stays && typedOnce });
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Which suggestion the keyboard is on. -1 means the caret is still in the
  // text, which is where it starts: arrowing down should reach the first
  // option, not skip it.
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const generatedId = useId();
  const inputId = testId || generatedId;
  const options = stays ? staySuggestions(value, ownCities, suggestions) : suggestions;
  const listVisible = open && (options.length > 0 || loading);

  useEffect(() => {
    let current = true;
    setSuggestions([]);
    setActive(-1);
    setLoading(false);
    if (!value || value.length < 2) {
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/locations/autocomplete`, { params: subType ? { q: value, sub_type: subType } : { q: value } });
        if (!current) return;
        setSuggestions(data.data || []);
        setActive(-1);
      } catch (e) {
        if (current) setSuggestions([]);
      } finally {
        if (current) setLoading(false);
      }
    }, 250);
    return () => {
      current = false;
      clearTimeout(debounceRef.current);
    };
  }, [value, subType]);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (s) => {
    onChange(stays ? s.city : pickValue ? pickValue(s) : s.code); // the IATA code by default, which is what the flight search sends upstream; a stay searches a city by its name
    onSelect?.(s);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (!open || options.length === 0) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      // Only when something is highlighted: otherwise Enter belongs to the
      // form, and swallowing it would stop a search being submitted.
      if (active >= 0) {
        e.preventDefault();
        choose(options[active]);
      }
    }
  };

  // Shared by both input shapes so the two cannot drift apart.
  const comboProps = {
    role: "combobox",
    "aria-expanded": listVisible,
    "aria-controls": listVisible ? `${inputId}-suggestions` : undefined,
    "aria-autocomplete": "list",
    "aria-activedescendant": listVisible && active >= 0 && active < options.length ? `${inputId}-opt-${active}` : undefined,
    onKeyDown,
  };

  const Icon = { plane: AirplaneTilt, takeoff: AirplaneTakeoff, landing: AirplaneLanding, search: MagnifyingGlass }[icon] || MapPin;

  return (
    <div ref={containerRef} className="relative w-full">
      {naked ? (
        <input
          id={inputId}
          data-testid={testId}
          aria-label={label || placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); setTypedOnce(true); }}
          onFocus={openOnFocus ? () => setOpen(true) : undefined}
          placeholder={placeholder}
          {...comboProps}
          className="w-full border-0 h-8 focus:outline-none text-base font-bold text-touki-black placeholder:text-touki-muted placeholder:font-normal bg-transparent"
          autoComplete="off"
        />
      ) : (
        <div className={`tk-search-place flex items-center gap-2 px-3 ${compact ? "min-h-[44px] rounded-md" : "min-h-[56px] rounded-lg"} bg-white border border-touki focus-within:border-touki-black`}>
          <Icon size={18} weight={isNativeApp() ? "regular" : "bold"} className={`text-touki-muted shrink-0 ${icon === "plane" ? "rotate-[-45deg]" : ""}`} aria-hidden="true" />
          <div className="flex-1 min-w-0">
            {label && <label htmlFor={inputId} className="block text-label-sm font-medium text-touki-muted">{label}</label>}
            <input
              data-testid={testId}
              id={inputId}
              aria-label={label || placeholder}
              value={value}
              onChange={(e) => { onChange(e.target.value); setOpen(true); setTypedOnce(true); }}
              onFocus={openOnFocus ? () => setOpen(true) : undefined}
              placeholder={placeholder}
              required={required}
              {...comboProps}
              className={`w-full border-0 px-0 ${label ? "h-7" : "h-8"} focus:outline-none text-body-md font-semibold text-touki-black placeholder:text-touki-muted placeholder:font-normal bg-transparent`}
              autoComplete="off"
            />
          </div>
        </div>
      )}
      {listVisible && (
        <div
          id={`${inputId}-suggestions`}
          role="listbox"
          className="absolute z-dropdown top-full inset-x-0 mt-1 bg-white border border-touki rounded-lg shadow-tk-pop max-h-80 overflow-auto"
          data-testid={`${testId}-suggestions`}
        >
          {loading && <div className="px-4 py-3 text-label-md text-touki-muted">{searching}</div>}
          {options.map((s, i) => (
            <button
              key={`${s.type}-${s.code}-${s.city}-${s.name}`}
              id={`${inputId}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(s)}
              className={`w-full min-h-11 text-start px-4 py-2.5 border-b border-touki last:border-b-0 flex items-center gap-2 ${i === active ? "bg-touki-light" : "hover:bg-touki-light"}`}
              data-testid={stays ? `suggestion-city-${fold(s.city).replace(/ /g, "-")}` : `suggestion-${s.code}`}
            >
              <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0 bg-touki-light text-touki-black" aria-hidden="true">
                {s.type === "airport" ? <AirplaneTilt size={14} weight="bold" /> : <MapPin size={14} weight="bold" />}
              </div>
              <div className="min-w-0 flex-1">
                {stays ? (
                  <>
                    <div className="text-body-sm font-semibold text-touki-black truncate">{s.city}</div>
                    <div className="text-eyebrow text-touki-muted truncate">{[s.type === "stay" ? words.stayCity : "", s.country].filter(Boolean).join(" · ")}</div>
                  </>
                ) : (
                  <>
                    <div className="text-body-sm font-semibold text-touki-black truncate">{s.city} <span className="text-touki-muted-strong" translate="no">({s.code})</span></div>
                    <div className="text-eyebrow text-touki-muted truncate">{s.name} · {s.country}</div>
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
